import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { useApi } from '@src/app/context/Api';
import { CodesTable } from '@src/entities/Code/ui/components/tables/Codes';
import { User } from '@src/entities/User/ui/components/User';
import { FormModal } from '@src/features/Codes/ManageCode/components/FormModal';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { motion } from 'framer-motion';
import { isEmpty, isUndefined } from 'lodash';
import React, { useEffect, useState } from 'react';

/**
 * Page: Codes
 *
 * Provides the user with a screen listing the existing codes.
 *
 * @returns {Element}
 * @constructor
 */
export function Codes() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [currentEntity, setCurrentEntity] = useState({});

  const [isLoadingQueries, setIsLoadingQueries] = useState(false);

  const { clearApiCache, useApiMutation, useApiQueries } = useApi('Code');

  const { assets, isLoadingAssets } = useAssets([
    {
      apiKey: 'Code',
      payload: { page: 0, size: 9999, sort: 'categoryCode,asc' },
      queryKey: 'ListCodeCategories',
    },
  ]);

  const [theQueries, setTheQueries] = useState([]);

  const queries = useApiQueries(theQueries);

  const [SaveCode] = useApiMutation('SaveCode');

  const { toast } = useToast();

  /**
   * Sets the default query to be used based on the assets.
   */
  useEffect(() => {
    if (
      isEmpty(theQueries) &&
      !isUndefined(assets?.ListCodeCategories?.data?.content?.[0]?.categoryCode) &&
      !isEmpty(assets?.ListCodeCategories?.data?.content?.[0]?.categoryCode)
    ) {
      const categoryCode = assets?.ListCodeCategories?.data?.content?.[0]?.categoryCode;

      setTheQueries([
        {
          apiKey: 'Code',
          payload: {
            categoryCode,
            page: 0,
            size: 9999,
            sort: 'codeId,asc',
          },
          queryKey: `ListCodes-${categoryCode}`,
        },
      ]);
    }
  }, [assets, theQueries]);

  /**
   * Determines the overall loading state of all queries.
   */
  useEffect(() => {
    if (
      !isEmpty(queries) &&
      Object.keys(queries).length === theQueries.length &&
      isEmpty(
        Object.keys(queries).filter((assetKey) => {
          const query = queries[assetKey];
          return !(!query?.isLoading && !query?.isFetching);
        })
      )
    ) {
      setIsLoadingQueries(false);
    } else {
      setIsLoadingQueries(true);
    }
  }, [queries, theQueries]);

  /**
   * handleSave
   *
   * Handles the saving of a code.
   *
   * @param payload
   * @returns {Promise<void>}
   */
  const handleSave = async (payload = {}) => {
    const toastId = 'save-code';

    toast.show({
      ariaLoadingLabel: 'Saving Code',
      autoClose: false,
      id: `${toastId}-info`,
      isLoading: true,
      message: 'Code is preparing to save.',
      title: 'Saving Code...',
      variant: 'info',
    });

    await SaveCode(payload, {
      onError: () => {
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-error`,
          message: `Unable to save code.`,
          title: 'Code Save Failed',
          variant: 'error',
        });
      },
      onSuccess: async () => {
        await clearApiCache([{ apiKey: 'Code', queryKeys: ['ListCodes'] }]);
        Object.keys(queries).forEach((queryKey) => {
          const query = queries[queryKey];
          if (query?.refetch) {
            query.refetch();
          }
        });

        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-success`,
          message: `Code has been saved.`,
          title: 'Saved Code',
          variant: 'success',
        });
      },
    });
  };

  return (
    <ErrorHandler location="src/pages/private/Admin/Codes/Codes.jsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Grid>
          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            <Flex alignItems="center" direction="row">
              <Heading offset={0}>Codes</Heading>
              <User.Capability attributes={['create']} resource="Admin:Codes">
                <Button
                  before={<IconSymbol icon="add" />}
                  css={{
                    marginLeft: themeConfiguration?.theme?.space?.md,
                  }}
                  data-testid="create-button"
                  isDisabled={isLoadingAssets}
                  onClick={() => {
                    setIsFormModalOpen(true);
                    setCurrentEntity({});
                  }}
                  size="$sm"
                  variant="outline"
                >
                  Create
                </Button>
              </User.Capability>
            </Flex>
          </Grid.Col>
          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            <CodesTable
              assets={assets}
              isLoading={isLoadingAssets || isLoadingQueries}
              queries={queries}
              setCurrentEntity={setCurrentEntity}
              setIsFormModalOpen={setIsFormModalOpen}
              setTheQueries={setTheQueries}
            />
            {isFormModalOpen && (
              <FormModal
                assets={assets}
                currentEntity={currentEntity}
                handleSave={handleSave}
                isOpen={isFormModalOpen}
                setCurrentEntity={setCurrentEntity}
                setIsOpen={setIsFormModalOpen}
              />
            )}
          </Grid.Col>
        </Grid>
      </motion.div>
    </ErrorHandler>
  );
}
